/**
 * The conditional removal module: finds the items a guarded action affects
 * and builds the rule-removed outcome, without mutating inputs. Queries
 * return entries `[{ index, item, ruleIndexes }]` — `ruleIndexes` holds the
 * dependent's rules that point at the affected triggers, so removal splices
 * exactly those rules and a dependent with surviving rules stays
 * conditioned. Entries `[{ index, item, ruleIndexes }]` from the placement
 * validator carry its invalid rule indexes. Queries must run over the same
 * `items` array the builder consumes (reference identity). Guards: Checklist
 * remove, drag, insert, locked-edit; parent section-delete, section-move
 * and orphan-move gates. Landed actions restore via `previousItems` on
 * cancel; detect-first actions no-op on cancel.
 */
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

/**
 * Entries of items outside sourceItems whose rules are triggered by one of
 * them, each with the matching rule indexes. sourceItems must hold the same
 * references as items.
 */
export function getDependentItems({ items, sourceItems }) {
  if (!_.isArray(items) || !_.isArray(sourceItems)) {
    return []
  }

  const sourceIds = new Set(
    sourceItems.map(item => utils.getEntityId(item?._id)).filter(Boolean)
  )

  return items
    .map((item, index) => ({ item, index }))
    .filter(entry => !sourceItems.includes(entry.item))
    .reduce((entries, entry) => {
      const ruleIndexes = _.reduce(
        entry.item?.visibilityRules,
        (matchingIndexes, rule, ruleIndex) => {
          if (sourceIds.has(utils.getEntityId(rule?.triggerItemId))) {
            matchingIndexes.push(ruleIndex)
          }

          return matchingIndexes
        },
        []
      )

      if (_.isEmpty(ruleIndexes)) {
        return entries
      }

      entries.push({ index: entry.index, item: entry.item, ruleIndexes })
      return entries
    }, [])
}

/**
 * Copy of items with the affected rules spliced out at every errored entry;
 * items whose rules are all removed end with an empty `visibilityRules`
 * array. Inputs are never mutated.
 */
export function removeInvalidVisibilityRules({ items, itemsWithError }) {
  if (!_.isArray(items)) {
    return []
  }

  if (!_.isArray(itemsWithError)) {
    return [...items]
  }

  const itemsWithoutAffectedRules = [...items]

  itemsWithError.forEach(entry => {
    if (!_.isNumber(entry?.index) || !items[entry.index]) {
      return
    }

    if (
      !_.isArray(entry.item?.visibilityRules) ||
      _.isEmpty(entry.ruleIndexes)
    ) {
      return
    }

    itemsWithoutAffectedRules[entry.index] = {
      ...entry.item,
      visibilityRules: entry.item.visibilityRules.filter(
        (_, ruleIndex) => !entry.ruleIndexes.includes(ruleIndex)
      ),
    }
  })

  return itemsWithoutAffectedRules
}

/**
 * Removal dialog payload for a landed action: the rule-removed items, the
 * pre-action items a cancel restores, and the affected count.
 */
export function buildErrorDialogPayload({
  items,
  itemsWithError,
  previousItems,
}) {
  return {
    items: removeInvalidVisibilityRules({ items, itemsWithError }),
    previousItems,
    affectedCount: itemsWithError.length,
  }
}
